import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import NoDataText from 'employee-platform/shared/components/NoDataText'
import FleetCard from './FleetCard'
import palette from 'core-system/Themes/palette'
import FleetCardDetailsModal from './FleetCardDetailsModal'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints[3]}) {
    flex-direction: column;
  }
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const CardType = styled(Text)`
  font-weight: 600;
  padding-bottom: 8px;
`

const CardImageContainer = styled(FleetCard)`
  margin-right: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints[3]}) {
    margin-bottom: 16px;
  }
`

interface FleetCardsProps {
  isMobile: boolean
}

const FleetCards = React.memo((props: FleetCardsProps) => {
  const { isMobile } = props

  const { cards } = useSelector(
    (state: AppState) => state.employeePlatform.wallet
  )

  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false)
    setSelectedCard(null)
  }, [])

  const handleClickCard = useCallback(async (card) => {
    setSelectedCard(card)
    setShowDetailsModal(true)
  }, [])

  return (
    <Container>
      <Text variant='action1'>Fleet Cards</Text>
      <Text variant='body1' textColor={palette.grey.grey500}>
        Click on a card to view details
      </Text>
      <CardsContainer>
        {cards && cards.length > 0 ? (
          cards.map((card) => {
            return (
              <CardContainer>
                <CardType variant='body1' color={palette.primary.pink800}>
                  {card?.type[0].toUpperCase() + card?.type.slice(1)}
                </CardType>
                <CardImageContainer
                  key={card.internalId}
                  card={card}
                  size={isMobile ? 'small' : 'large'}
                  onPress={handleClickCard}
                />
              </CardContainer>
            )
          })
        ) : (
          <NoDataText text='YOU DO NOT HAVE ANY FLEET CARDS' />
        )}
      </CardsContainer>
      <FleetCardDetailsModal
        open={showDetailsModal}
        closeModal={handleCloseDetailsModal}
        card={selectedCard}
        isMobile={isMobile}
      />
    </Container>
  )
})

// Helps identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  FleetCards.displayName = 'FleetCards'
}

export default FleetCards
